/**
 * Client for the AI Data Explorer text→SQL service (Flask, proxied at /ai/*).
 *
 * The user's Supabase JWT is attached so the service can verify project
 * membership before running anything. The service returns rows + a generated
 * Persian title + optional chart; the SQL itself is re-guarded server-side on
 * every run, so storing it is safe.
 */
import { supabase } from '@/lib/supabase/client'
import { parseDefinition } from '@/features/form-builder/api/form-builder-api'

const BASE = (import.meta.env.VITE_AI_ASSISTANT_URL as string | undefined) || ''

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = json?.error?.message || json?.message || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return json as T
}

// ─── Project schema (data dictionary the LLM grounds on) ──────────────────────

export interface AiFormSchema {
  form_key: string
  instance_type: 'single' | 'repeatable'
  title: string
  fields: { key: string; type: string; label: string }[]
}

/** Published forms + their fields, shaped for the text→SQL prompt. */
export async function fetchProjectSchema(projectId: string): Promise<AiFormSchema[]> {
  const { data: forms, error } = await supabase
    .from('forms')
    .select('form_key, instance_type, title, current_version_id')
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .eq('is_published', true)
  if (error) throw error
  const versionIds = (forms ?? []).map((f) => f.current_version_id).filter(Boolean) as string[]
  if (versionIds.length === 0) return []

  const { data: versions } = await supabase
    .from('form_versions')
    .select('id, definition_snapshot')
    .in('id', versionIds)
  const defById = new Map((versions ?? []).map((v) => [v.id, v.definition_snapshot]))

  return (forms ?? []).map((f) => {
    const def = parseDefinition(defById.get(f.current_version_id as string))
    const fields = def.fields.map((fl) => ({
      key: fl.field_key,
      type: fl.field_type,
      label: fl.label?.fa ?? fl.field_key,
    }))
    for (const c of def.calculated_fields ?? []) {
      fields.push({ key: c.field_key, type: 'calculated', label: c.label?.fa ?? c.field_key })
    }
    const title = typeof f.title === 'string' ? f.title : (f.title as { fa?: string })?.fa ?? f.form_key
    return {
      form_key: f.form_key,
      instance_type: f.instance_type as 'single' | 'repeatable',
      title,
      fields,
    }
  })
}

// ─── Query + re-run ───────────────────────────────────────────────────────────

/** A sanitized-on-render Vega-Lite v6 spec (data injected client-side). */
export type VegaLiteSpec = Record<string, unknown>

export interface AiQueryResult {
  ok: boolean
  sql?: string
  dialect?: string
  title?: string
  explanation?: string | null
  vega_lite?: VegaLiteSpec | null
  columns?: string[]
  rows?: Record<string, unknown>[]
  row_count?: number
  truncated?: boolean
  message?: string
  assistant_message?: string
}

export async function aiQuery(
  projectId: string,
  prompt: string,
  forms: AiFormSchema[],
  history?: { role: string; content: string }[],
): Promise<AiQueryResult> {
  const res = await postJson<{ ok: boolean; result: AiQueryResult }>('/ai/api/explore/query', {
    projectId, prompt, forms, history, sync: true,
  })
  return res.result
}

/** Conversationally edit an existing query (sends current SQL + history; the service
 * computes a PHI-safe value digest server-side). */
export async function aiEdit(
  projectId: string,
  prompt: string,
  currentSql: string,
  forms: AiFormSchema[],
  history?: { role: string; content: string }[],
  dialect = 'postgres',
): Promise<AiQueryResult> {
  const res = await postJson<{ ok: boolean; result: AiQueryResult }>('/ai/api/explore/edit', {
    projectId, prompt, currentSql, dialect, forms, history, sync: true,
  })
  return res.result
}

export interface AiRunResult {
  ok: boolean
  columns?: string[]
  rows?: Record<string, unknown>[]
  row_count?: number
  truncated?: boolean
  message?: string
}

export async function aiRun(
  projectId: string,
  sql: string,
  dialect = 'postgres',
  limit?: number,
  offset?: number,
): Promise<AiRunResult> {
  return postJson<AiRunResult>('/ai/api/explore/run', { projectId, sql, dialect, limit, offset })
}
